import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTieneRol } from "@/lib/sesion";
import { api } from "@/lib/api";
import { CeldaFolio, CeldaProducto } from "@/components/IdentificacionSolicitud";
import { estadoTono, etiquetaEstado } from "@/components/estadoSolicitud";
import { descargarRespuestaBlob } from "@/lib/download";
import { Cpu, ChevronsRight, BookCheck, FileText, Loader2, Filter, X, LayoutGrid, Download, Search, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, UserX, Copy } from "lucide-react";
import { TransitionsModal } from "@/components/TransitionsModal";
import { DocumentosFinalesModal } from "@/components/DocumentosFinalesModal";
import { SectorNoHabilitadoPanel } from "@/components/SectorNoHabilitadoPanel";
import { useTelecomAccess } from "@/hooks/useTelecomAccess";
import { Alert, Button, Card, FormField, Input, PageContainer, PageHeader, Select } from "@/components/ui";

interface PageResp<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
}

export function TelecomPage() {
  const access = useTelecomAccess();

  if (access.loading) {
    return (
      <div className="flex items-center justify-center p-12 text-sm text-fg-subtle">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validando permisos…
      </div>
    );
  }
  if (!access.allowed) {
    return (
      <SectorNoHabilitadoPanel
        sectorNombre="Telecomunicaciones"
        sectorCodigo="TELECOM"
        titularRazonSocial={access.titularRazonSocial}
        sinContexto={access.sinContexto}
      />
    );
  }

  return (
    <PageContainer width="full">
      <PageHeader
        icon={<Cpu className="h-5 w-5" />}
        title="Telecomunicaciones"
        subtitle="Solicitudes de certificación del sector Telecomunicaciones."
        actions={
          <>
            <Link to="/telecom/servicios">
              <Button icon={<LayoutGrid className="h-4 w-4" />}>Servicios</Button>
            </Link>
            <Link to="/telecom/certificados/por-certificar">
              <Button variant="secondary" icon={<BookCheck className="h-4 w-4" />}>Por certificar</Button>
            </Link>
          </>
        }
      />

      <SolicitudesTab />
    </PageContainer>
  );
}

interface SolicitudRow {
  id: string;
  folio: string;
  titularRazonSocial: string;
  tramitadorRazonSocial: string | null;
  servicioCodigo: string | null;
  productoNombre: string;
  productoModelo: string;
  marca: string;
  numReferencia: string | null;
  /** Número de pre-solicitud NT-AAMMDD-NN (TEL-14). */
  preSolicitud: string | null;
  normaCodigo: string;
  instanciaId: string | null;
  estadoCodigo: string | null;
  cerrada: boolean;
  fechaCreacion: string | null;
  certificadoId: string | null;
  numCertificado: string | null;
  certificadoBaseId: string | null;
  certificadoBaseFirmadoDisponible: boolean;
  /** Responsable de la etapa actual (RF-03); solo llega para internos. */
  responsableId: string | null;
  responsableNombre: string | null;
  /** Documentos finales cargados (tipos configurados por servicio). */
  documentosFinales: number;
  /** Etiqueta de la acción de consulta («Ver carta aduana», …). */
  accionDocumentos: string;
  /**
   * Solicitudes no derivadas que comparten el mismo «Modelo principal».
   * Mayor que 1 ⇒ se marca la fila como modelo duplicado. A los externos solo
   * se les cuentan las suyas.
   */
  solicitudesMismoModelo: number;
}

// Internos del OC: ven la columna Responsable y el filtro «Sin asignar» (RF-03).
const ROLES_INTERNOS_OC = [
  "ADMIN_SICEN", "GERENTE_CALIDAD", "AUXILIAR_CALIDAD", "COORDINADOR_TECNICO",
  "EVALUADOR_TECNICO", "RESPONSABLE_TELECOM", "RESPONSABLE_ELECTRICA",
  "RESPONSABLE_EFICIENCIA", "RESPONSABLE_CERTIFICADOS", "FACTURACION",
  "AUDITOR_INTERNO", "DIRECCION", "VIGILANCIA",
];

interface CatalogoItem { codigo: string; nombre: string; }
type SortField = "folio" | "empresa" | "servicio" | "estatus" | "fechaCreacion";
const PAGE_SIZES = [10, 25, 50, 100];

function SolicitudesTab() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  // Identidad efectiva: bajo suplantación el token sigue siendo el del
  // administrador, y con él esta pantalla mostraba columnas y filtros de
  // interno del OC a un usuario externo.
  const esInterno = useTieneRol(...ROLES_INTERNOS_OC);
  const [searchParams, setSearchParams] = useSearchParams();

  // "En proceso" se conserva en la URL para que el deep-link de la campana
  // (?filtro=en-proceso) siga funcionando. "Sin asignar" (RF-03) usa el mismo
  // mecanismo para poder compartir el enlace.
  const enProceso = searchParams.get("filtro") === "en-proceso";
  const sinAsignar = esInterno && searchParams.get("filtro") === "sin-asignar";

  const [avanzar, setAvanzar] = useState<SolicitudRow | null>(null);
  const [docsFinalesDe, setDocsFinalesDe] = useState<SolicitudRow | null>(null);

  // Filtros
  const [qInput, setQInput] = useState("");
  const [qApplied, setQApplied] = useState("");
  const [servicio, setServicio] = useState("");
  const [estado, setEstado] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  // Paginación + orden
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);
  const [sortField, setSortField] = useState<SortField>("fechaCreacion");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  // Debounce de la caja de búsqueda.
  useEffect(() => {
    const t = setTimeout(() => { setQApplied(qInput.trim()); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [qInput]);

  // Catálogos para selects.
  const qServicios = useQuery<CatalogoItem[]>({
    queryKey: ["telecom-servicios-cat"],
    queryFn: async () => (await api.get<CatalogoItem[]>("/api/sector/telecom/servicios")).data,
    staleTime: 5 * 60 * 1000,
  });
  const qEstados = useQuery<CatalogoItem[]>({
    queryKey: ["telecom-estados-cat"],
    queryFn: async () => (await api.get<CatalogoItem[]>("/api/sector/telecom/estados")).data,
    staleTime: 5 * 60 * 1000,
  });

  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("size", String(size));
  params.set("sort", `${sortField},${sortDir}`);
  if (qApplied) params.set("q", qApplied);
  if (servicio) params.set("servicio", servicio);
  if (estado) params.set("estado", estado);
  if (fechaDesde) params.set("fechaDesde", fechaDesde);
  if (fechaHasta) params.set("fechaHasta", fechaHasta);
  if (enProceso) params.set("enProceso", "true");
  if (sinAsignar) params.set("sinAsignar", "true");
  const queryString = params.toString();

  const q = useQuery({
    queryKey: ["solicitudes-telecom", queryString],
    queryFn: async () =>
      (await api.get<PageResp<SolicitudRow>>(`/api/sector/telecom/solicitudes?${queryString}`)).data,
  });

  const setFiltroRapido = (v: "" | "en-proceso" | "sin-asignar") => {
    const next = new URLSearchParams(searchParams);
    if (v) next.set("filtro", v); else next.delete("filtro");
    setSearchParams(next, { replace: true });
    setPage(0);
  };

  const toggleSort = (f: SortField) => {
    if (sortField === f) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(f); setSortDir(f === "fechaCreacion" ? "desc" : "asc"); }
    setPage(0);
  };

  const limpiarFiltros = () => {
    setQInput(""); setQApplied(""); setServicio(""); setEstado("");
    setFechaDesde(""); setFechaHasta(""); setPage(0);
  };

  const hayFiltros = !!(qApplied || servicio || estado || fechaDesde || fechaHasta);
  const data = q.data;
  const totalElements = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 0;
  const filas = data?.content ?? [];
  const desde = totalElements === 0 ? 0 : page * size + 1;
  const hasta = Math.min(page * size + filas.length, totalElements);

  return (
    <>
      {/* Barra de filtros */}
      <Card padding="sm">
        <div className="flex flex-wrap items-end gap-3">
          <FormField label="Buscar" className="min-w-[240px] flex-1">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-faint" />
              <Input
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                placeholder="Folio, pre-solicitud, referencia, modelo, marca, núm. certificado o razón social…"
                className="pl-9"
              />
            </div>
          </FormField>
          <FormField label="Servicio">
            <Select value={servicio} onChange={(e) => { setServicio(e.target.value); setPage(0); }}>
              <option value="">Todos</option>
              {qServicios.data?.map((s) => (<option key={s.codigo} value={s.codigo}>{s.nombre}</option>))}
            </Select>
          </FormField>
          <FormField label="Estatus">
            <Select value={estado} onChange={(e) => { setEstado(e.target.value); setPage(0); }}>
              <option value="">Todos</option>
              {qEstados.data?.map((s) => (<option key={s.codigo} value={s.codigo}>{s.nombre}</option>))}
            </Select>
          </FormField>
          <FormField label="Desde">
            <Input type="date" value={fechaDesde} onChange={(e) => { setFechaDesde(e.target.value); setPage(0); }} />
          </FormField>
          <FormField label="Hasta">
            <Input type="date" value={fechaHasta} onChange={(e) => { setFechaHasta(e.target.value); setPage(0); }} />
          </FormField>
          {hayFiltros && (
            <Button variant="secondary" icon={<X className="h-3.5 w-3.5" />} onClick={limpiarFiltros}>
              Limpiar
            </Button>
          )}
        </div>
      </Card>

      <Card padding="none">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-edge/60 px-4 py-2.5">
          <span className="text-xs uppercase tracking-wide text-fg-subtle">
            {totalElements === 0 ? "Sin resultados" : `${totalElements} solicitud${totalElements === 1 ? "" : "es"}`}
          </span>
          <div className="flex items-center gap-1 rounded border bg-surface-2 p-0.5 text-xs">
            <button type="button" onClick={() => setFiltroRapido("")}
                    className={`inline-flex items-center gap-1 rounded px-2.5 py-1 transition ${!enProceso && !sinAsignar ? "bg-surface font-medium text-fg shadow-sm" : "text-fg-muted hover:text-fg"}`}>
              Todas
            </button>
            <button type="button" onClick={() => setFiltroRapido("en-proceso")}
                    className={`inline-flex items-center gap-1 rounded px-2.5 py-1 transition ${enProceso ? "bg-surface font-medium text-estado-warn shadow-sm" : "text-fg-muted hover:text-fg"}`}>
              <Filter className="h-3 w-3" /> En proceso
            </button>
            {esInterno && (
              <button type="button" onClick={() => setFiltroRapido("sin-asignar")}
                      className={`inline-flex items-center gap-1 rounded px-2.5 py-1 transition ${sinAsignar ? "bg-surface font-medium text-estado-warn shadow-sm" : "text-fg-muted hover:text-fg"}`}>
                <UserX className="h-3 w-3" /> Sin asignar
              </button>
            )}
          </div>
        </div>

        {q.isLoading ? (
          <div className="p-6 text-sm text-fg-subtle">Cargando…</div>
        ) : q.error ? (
          <div className="p-4"><Alert variant="warning">Error cargando solicitudes.</Alert></div>
        ) : filas.length === 0 ? (
          <div className="p-6 text-center text-sm text-fg-subtle">
            {hayFiltros || enProceso || sinAsignar ? (
              <>No hay solicitudes que coincidan con los filtros.{" "}
                <button type="button" onClick={() => { limpiarFiltros(); setFiltroRapido(""); }}
                        className="inline-flex items-center gap-1 text-primary-strong hover:underline">
                  <X className="h-3 w-3" /> Limpiar
                </button>
              </>
            ) : "No hay solicitudes registradas."}
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-surface-2">
              <tr>
                <SortableTh label="Folio" field="folio" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Titular" field="empresa" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                <Th>Tramitador</Th>
                <SortableTh label="Trámite" field="servicio" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                <Th>Producto</Th>
                <Th>Norma</Th>
                <SortableTh label="Fecha" field="fechaCreacion" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Estado" field="estatus" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                {esInterno && <Th>Responsable</Th>}
                <Th>Acciones</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-edge bg-surface">
              {filas.map((r) => (
                <tr key={r.id} className="cursor-pointer transition hover:bg-primary-tint"
                    onClick={() => navigate(`/sector/telecom/solicitudes/${r.id}`)}>
                  <td className="px-4 py-3 align-top">
                    <CeldaFolio folio={r.folio} preSolicitud={r.preSolicitud} numReferencia={r.numReferencia} />
                  </td>
                  <td className="px-4 py-3 align-top font-medium">{r.titularRazonSocial}</td>
                  <td className="px-4 py-3 align-top text-xs">{r.tramitadorRazonSocial ?? "—"}</td>
                  <td className="px-4 py-3 align-top text-xs">{r.servicioCodigo ?? "—"}</td>
                  <td className="px-4 py-3 align-top">
                    <CeldaProducto producto={r.productoNombre} marca={r.marca} modelos={r.productoModelo} />
                    {r.solicitudesMismoModelo > 1 && (
                      <span
                        title={`El modelo «${r.productoModelo}» aparece en ${r.solicitudesMismoModelo} solicitudes`}
                        className="mt-1 inline-flex items-center gap-1 rounded border border-estado-warn/25 bg-estado-warn-bg px-1.5 py-0.5 text-[11px] font-medium text-estado-warn"
                      >
                        <Copy className="h-3 w-3" /> Modelo duplicado
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top text-xs">{r.normaCodigo}</td>
                  <td className="px-4 py-3 align-top text-xs">{formatFecha(r.fechaCreacion)}</td>
                  <td className="px-4 py-3 align-top">
                    <Badge tone={estadoTono(r.estadoCodigo, r.cerrada)}>{etiquetaEstado(r.estadoCodigo)}</Badge>
                  </td>
                  {esInterno && (
                    <td className="px-4 py-3 align-top text-xs">
                      {r.responsableNombre ? (
                        r.responsableNombre
                      ) : r.cerrada ? (
                        <span className="text-fg-faint">—</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 font-medium text-estado-warn">
                          <UserX className="h-3 w-3" /> Sin asignar
                        </span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-2 align-top">
                    {r.certificadoId ? (
                      <button type="button"
                        onClick={(e) => { e.stopPropagation(); navigate(`/telecom/certificados/${r.certificadoId}`); }}
                        className="inline-flex items-center gap-1 rounded border border-estado-ok/25 bg-estado-ok-bg px-2 py-1 text-xs font-medium text-estado-ok transition hover:bg-estado-ok/15"
                        title={r.numCertificado ?? undefined}>
                        <FileText className="h-3 w-3" /> Ver certificado
                      </button>
                    ) : esInterno && r.cerrada && r.estadoCodigo === "ACEPTADA" &&
                      (r.servicioCodigo === "CERTIFICADO_UNICO" || r.servicioCodigo === "REEXPEDICION_PC" ||
                        (r.servicioCodigo ?? "").startsWith("AMPLIACION")) ? (
                      <button type="button"
                        onClick={(e) => { e.stopPropagation(); navigate(`/telecom/certificados/nuevo?solicitudId=${r.id}`); }}
                        className="inline-flex items-center gap-1 rounded border border-primary-strong px-2 py-1 text-xs font-medium text-primary-strong transition hover:bg-primary-tint">
                        <FileText className="h-3 w-3" /> Emitir certificado
                      </button>
                    ) : r.cerrada && r.documentosFinales > 0 ? (
                      <button type="button"
                        onClick={(e) => { e.stopPropagation(); setDocsFinalesDe(r); }}
                        className="inline-flex items-center gap-1 rounded border border-estado-ok/25 bg-estado-ok-bg px-2 py-1 text-xs font-medium text-estado-ok transition hover:bg-estado-ok/15"
                        title={`${r.documentosFinales} documento(s) final(es)`}>
                        <FileText className="h-3 w-3" /> {r.accionDocumentos}
                      </button>
                    ) : r.servicioCodigo === "COPIA_FIEL" && r.certificadoBaseId && r.certificadoBaseFirmadoDisponible && r.cerrada ? (
                      <DescargarFirmadoButton certificadoBaseId={r.certificadoBaseId} />
                    ) : r.instanciaId && !r.cerrada ? (
                      <button onClick={(e) => { e.stopPropagation(); setAvanzar(r); }}
                        className="inline-flex items-center gap-1 rounded border border-estado-info/25 px-2 py-1 text-xs text-estado-info transition hover:bg-estado-info-bg">
                        <ChevronsRight className="h-3 w-3" /> Avanzar
                      </button>
                    ) : (
                      <span className="text-xs text-fg-faint">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}

        {/* Paginador */}
        {totalElements > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-edge/60 px-4 py-2.5 text-xs text-fg-muted">
            <div className="flex items-center gap-2">
              <span>Mostrando {desde}–{hasta} de {totalElements}</span>
              <span className="text-fg-faint">·</span>
              <label className="flex items-center gap-1">
                Por página:
                <select value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
                        className="rounded border px-1.5 py-1 text-xs">
                  {PAGE_SIZES.map((n) => (<option key={n} value={n}>{n}</option>))}
                </select>
              </label>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" disabled={page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}
                      className="inline-flex items-center gap-1 rounded border px-2 py-1 transition disabled:opacity-40 hover:bg-surface-2">
                <ChevronLeft className="h-3.5 w-3.5" /> Anterior
              </button>
              <span>Página {totalPages === 0 ? 0 : page + 1} de {totalPages}</span>
              <button type="button" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}
                      className="inline-flex items-center gap-1 rounded border px-2 py-1 transition disabled:opacity-40 hover:bg-surface-2">
                Siguiente <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </Card>

      {avanzar?.instanciaId && (
        <TransitionsModal
          open={!!avanzar}
          solicitudId={avanzar.id}
          instanciaId={avanzar.instanciaId}
          folio={avanzar.folio}
          onClose={() => setAvanzar(null)}
          onDone={async () => {
            setAvanzar(null);
            await qc.invalidateQueries({ queryKey: ["solicitudes-telecom"] });
          }}
        />
      )}

      {docsFinalesDe && (
        <DocumentosFinalesModal
          solicitudId={docsFinalesDe.id}
          folio={docsFinalesDe.folio}
          onClose={() => setDocsFinalesDe(null)}
        />
      )}
    </>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-fg-subtle">{children}</th>;
}

function SortableTh({
  label, field, sortField, sortDir, onSort,
}: {
  label: string; field: SortField; sortField: SortField; sortDir: "asc" | "desc"; onSort: (f: SortField) => void;
}) {
  const active = sortField === field;
  return (
    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-fg-subtle">
      <button type="button" onClick={() => onSort(field)}
              className={`inline-flex items-center gap-1 transition hover:text-fg ${active ? "text-fg" : ""}`}>
        {label}
        {active ? (
          sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
        ) : (
          <ArrowUp className="h-3 w-3 opacity-20" />
        )}
      </button>
    </th>
  );
}

function formatFecha(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return "—";
  }
}

function Badge({ tone, children }: { tone: "ok" | "info" | "warn" | "err"; children: React.ReactNode }) {
  const cls = {
    ok: "bg-estado-ok-bg text-estado-ok",
    info: "bg-estado-info-bg text-estado-info",
    warn: "bg-estado-warn-bg text-estado-warn",
    err: "bg-estado-err-bg text-estado-err",
  }[tone];
  return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{children}</span>;
}

function DescargarFirmadoButton({ certificadoBaseId }: { certificadoBaseId: string }) {
  const [loading, setLoading] = useState(false);
  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setLoading(true);
    try {
      const resp = await api.get(
        `/api/sector/telecom/certificados/${certificadoBaseId}/firmado`,
        { responseType: "blob" },
      );
      descargarRespuestaBlob(resp, "Certificado-firmado.pdf");
    } catch {
      // ignorar; el botón solo aparece cuando el firmado está disponible
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className="inline-flex items-center gap-1 rounded border border-estado-ok/25 bg-estado-ok-bg px-2 py-1 text-xs font-medium text-estado-ok transition hover:bg-estado-ok/15 disabled:opacity-50"
    >
      {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
      Descargar firmado
    </button>
  );
}
